import { Card, Table, Button, Space, Empty, Input, Tag, message, Popconfirm, Modal } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseCircleOutlined, CameraOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import type { AdjacentCrop, AdjacentCropGeoJSON } from "../../../types/adjacent-crop";
import { adjacentCropService } from "../../../services/api";
import { PhotoGalleryModal } from "../../../components/PhotoGalleryModal";
import { AdjacentCropForm } from "./AdjacentCropForm";
import { AdjacentCropReviewForm } from "./AdjacentCropReviewForm";
import dayjs from "dayjs";

interface AdjacentCropsSectionProps {
  auditId: number;
  farmId: number;
  onRefresh?: () => void;
  /** Fires with the row count whenever the data loads — used by the
   *  parent to render a tab badge. */
  onCountChange?: (count: number) => void;
  /** When true, hides/disables every write action. Used when the
   *  parent audit is finalised. */
  readOnly?: boolean;
}

/**
 * AdjacentCropsSection Component
 * Displays adjacent crops for a specific audit/farm
 */
export const AdjacentCropsSection = ({
  auditId,
  farmId,
  onRefresh,
  onCountChange,
  readOnly = false,
}: AdjacentCropsSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<AdjacentCrop[]>([]);
  const [loading, setLoading] = useState(false);
  const [photoGalleryVisible, setPhotoGalleryVisible] = useState(false);
  const [selectedAdjacentCrop, setSelectedAdjacentCrop] = useState<{ id: string; name: string } | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedCropForEdit, setSelectedCropForEdit] = useState<AdjacentCrop | null>(null);
  const [selectedCropForReview, setSelectedCropForReview] = useState<AdjacentCrop | null>(null);
  // Inline "Dar de baja" modal — separate from the review wizard so
  // the auditor can close a crop without walking through the full
  // review (e.g. when it's no longer there at all).
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [cropForClose, setCropForClose] = useState<AdjacentCrop | null>(null);
  const [closeReason, setCloseReason] = useState("");
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (farmId && auditId) {
      loadData();
    }
  }, [farmId, auditId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await adjacentCropService.getOpenAdjacentCropsForFarm(farmId, auditId);
      
      // Handle GeoJSON FeatureCollection format
      let adjacentCrops: AdjacentCrop[] = [];
      const rawCrops = result.adjacent_crops || [];
      
      if (rawCrops && typeof rawCrops === 'object' && 'features' in rawCrops) {
        // It's a FeatureCollection, extract features and convert to AdjacentCrop.
        // Carry the geometry over from `feature.geometry` so the
        // review wizard's map can render it instead of starting empty.
        const features = (rawCrops as any).features || [];
        adjacentCrops = features.map((feature: AdjacentCropGeoJSON) => ({
          ...feature.properties,
          id: feature.id || feature.properties.id,
          geom: feature.geometry ?? null,
        }));
      } else if (Array.isArray(rawCrops)) {
        adjacentCrops = rawCrops as AdjacentCrop[];
      }
      
      setData(adjacentCrops);
      onCountChange?.(adjacentCrops.length);
    } catch (error: any) {
      message.error("Error al cargar cultivos adyacentes");
      setData([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate(`/adjacent-crops/create?auditId=${auditId}`);
  };

  const handleEdit = (crop: AdjacentCrop) => {
    navigate(`/adjacent-crops/edit/${crop.id}`);
  };

  const handleReview = (crop: AdjacentCrop) => {
    setSelectedCropForReview(crop);
    setReviewModalVisible(true);
  };

  const handleOpenClose = (crop: AdjacentCrop) => {
    setCropForClose(crop);
    setCloseReason("");
    setCloseModalOpen(true);
  };

  const handleConfirmClose = async () => {
    if (!cropForClose) return;
    const reason = closeReason.trim();
    if (!reason) {
      message.warning("Indica el motivo del cierre");
      return;
    }
    setClosing(true);
    try {
      const dayjs = (await import("dayjs")).default;
      await adjacentCropService.closeAdjacentCrop(cropForClose.id, {
        closed_date: dayjs().format("YYYY-MM-DD"),
        comment_closed: reason,
        audit_id: auditId,
      } as any);
      message.success("Cultivo dado de baja");
      setCloseModalOpen(false);
      setCropForClose(null);
      setCloseReason("");
      loadData();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      message.error(error?.message || "No se pudo dar de baja el cultivo");
    } finally {
      setClosing(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adjacentCropService.deleteAdjacentCrop(id);
      message.success("Cultivo adyacente eliminado");
      loadData();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      message.error("Error al eliminar");
    }
  };

  const handleViewPhotos = (id: string, name: string) => {
    setSelectedAdjacentCrop({ id, name });
    setPhotoGalleryVisible(true);
  };

  // Row "Fotos" button — opens the gallery modal with upload
  // capability. The modal asks the section (via getTargetId) for the
  // AdjacentCropReview id for this audit, creating one on the fly
  // when none exists yet.
  const handleOpenPhotos = (crop: AdjacentCrop) => {
    setSelectedAdjacentCrop({ id: crop.id, name: crop.name });
    setPhotoGalleryVisible(true);
  };

  const resolveAdjacentCropReviewId = async (
    crop: AdjacentCrop,
  ): Promise<string> => {
    // 1. Look at the existing reviews for this crop and pick the one
    //    tied to the current audit, if any.
    const detail = await adjacentCropService.getAdjacentCrop(crop.id);
    const existing = (detail.reviews || []).find((r) => r.audit === auditId);
    if (existing) return existing.id;
    // 2. None exists — create a minimal review so the photo has a
    //    parent FK to attach to. Comment + species are required by
    //    the endpoint; we inherit the crop's current species and
    //    leave a marker comment so the auditor knows it was
    //    auto-created.
    const created = await adjacentCropService.createReviewFromAudit({
      audit_id: auditId,
      adjacent_crop_id: crop.id,
      species: crop.species,
      variety: crop.variety,
      comment: "Revisión auto-creada para adjuntar fotos.",
      estimated_surface: crop.total_estimated_surface ?? 0,
    });
    return created.id;
  };

  const handlePhotoDeleted = () => {
    loadData();
    if (onRefresh) onRefresh();
  };

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    loadData();
    if (onRefresh) onRefresh();
    message.success("Cultivo adyacente creado exitosamente");
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedCropForEdit(null);
    loadData();
    if (onRefresh) onRefresh();
    message.success("Cultivo adyacente actualizado exitosamente");
  };

  const handleReviewSuccess = () => {
    setReviewModalVisible(false);
    setSelectedCropForReview(null);
    loadData();
    if (onRefresh) onRefresh();
    message.success("Revisión creada exitosamente");
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Especie",
      dataIndex: "species_name",
      key: "species_name",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Variedad",
      dataIndex: "variety_name",
      key: "variety_name",
      width: 110,
      ellipsis: true,
      render: (value: string) => value || "Desconocida",
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (value: string) => (
        <Tag color={value === "open" ? "green" : "red"}>
          {value === "open" ? "Abierto" : "Cerrado"}
        </Tag>
      ),
    },
    {
      title: "Detección",
      dataIndex: "detection_date",
      key: "detection_date",
      width: 105,
      render: (value: string) => value ? dayjs(value).format("DD/MM/YYYY") : '-',
    },
    {
      title: "Sup. (ha)",
      dataIndex: "total_estimated_surface",
      key: "total_estimated_surface",
      width: 90,
      align: "center" as const,
      render: (value: number) => value ? value.toFixed(2) : '-',
    },
    {
      title: "Revisiones",
      dataIndex: "review_count",
      key: "review_count",
      width: 95,
      align: "center" as const,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 170,
      fixed: "right" as const,
      render: (_: any, record: AdjacentCrop) => {
        // Crops created in *this* audit can still be edited/deleted
        // outright. Pre-existing crops use the review wizard.
        const isCreatedInThisAudit = record.is_first_review_in_audit;
        const isClosed = record.status === "closed";
        return (
          <Space size="small">
            {isCreatedInThisAudit ? (
              <>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  title="Editar"
                  disabled={isClosed || readOnly}
                />
                <Popconfirm
                  title="¿Está seguro de eliminar este cultivo adyacente?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Sí"
                  cancelText="No"
                  disabled={readOnly}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    title="Eliminar"
                    disabled={readOnly}
                  />
                </Popconfirm>
              </>
            ) : (
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => handleReview(record)}
                title="Revisión (datos + mapa)"
                disabled={isClosed || readOnly}
              >
                Revisión
              </Button>
            )}
            {/* Row-level "Fotos" — gallery + upload. The modal
                auto-creates a placeholder review the first time the
                auditor adds a photo so it can be linked to the
                current audit. */}
            <Button
              size="small"
              icon={<CameraOutlined />}
              onClick={() => handleOpenPhotos(record)}
              title="Fotos"
              disabled={isClosed}
            />
            {/* Row-level "Dar de baja" — separate from the review
                wizard so the auditor can close the crop with just
                a reason, no full review walkthrough. Disabled when
                the crop already has a review for this audit (it's
                already considered "atendido" — would be
                contradictory to revisarlo y luego darlo de baja en
                el mismo paso). */}
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleOpenClose(record)}
              title={
                record.has_review_in_audit
                  ? "Esta variedad ya está revisada en esta auditoría"
                  : "Dar de baja"
              }
              disabled={
                isClosed || readOnly || !!record.has_review_in_audit
              }
            />
          </Space>
        );
      },
    },
  ];

  if (!farmId || !auditId) {
    return null;
  }

  return (
    <>
      <Card
        title="Cultivos Adyacentes"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            disabled={readOnly}
          >
            Crear
          </Button>
        }
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        {/* Row highlights: red for crops just given de baja in this
            audit, green when the auditor has saved a review in this
            audit. Red wins on conflict (closed implies the review is
            past tense). */}
        <style>{`
          .adjacent-crops-table .crop-closed-here-row td {
            background-color: #fff1f0 !important;
          }
          .adjacent-crops-table .crop-reviewed-row td {
            background-color: #f6ffed !important;
          }
        `}</style>
        <Table
          className="adjacent-crops-table planning-audits-table"
          size="small"
          dataSource={data}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 815 }}
          rowClassName={(record) => {
            if (
              record.status === "closed" &&
              record.closed_in_audit != null &&
              record.closed_in_audit === auditId
            ) {
              return "crop-closed-here-row";
            }
            if (record.has_review_in_audit) {
              return "crop-reviewed-row";
            }
            return "";
          }}
          locale={{
            emptyText: "No hay cultivos adyacentes registrados para esta finca"
          }}
        />
      </Card>

      {/* Create Adjacent Crop Modal */}
      <Modal
        title="Crear Cultivo Adyacente"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <AdjacentCropForm
          auditId={auditId}
          farmId={farmId}
          onSuccess={handleCreateSuccess}
          onCancel={() => setCreateModalVisible(false)}
        />
      </Modal>

      {/* Edit Adjacent Crop Modal */}
      <Modal
        title={`Editar Cultivo Adyacente: ${selectedCropForEdit?.name || ''}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedCropForEdit(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        {selectedCropForEdit && (
          <AdjacentCropForm
            auditId={auditId}
            farmId={farmId}
            adjacentCrop={selectedCropForEdit}
            onSuccess={handleEditSuccess}
            onCancel={() => {
              setEditModalVisible(false);
              setSelectedCropForEdit(null);
            }}
          />
        )}
      </Modal>

      {/* Review wizard — modal lives inside the component now. */}
      {selectedCropForReview && (
        <AdjacentCropReviewForm
          visible={reviewModalVisible}
          auditId={auditId}
          adjacentCrop={selectedCropForReview}
          onSuccess={handleReviewSuccess}
          onCancel={() => {
            setReviewModalVisible(false);
            setSelectedCropForReview(null);
          }}
        />
      )}

      {/* Row-level "Dar de baja" — quick reason → close on catalogue. */}
      <Modal
        title="Dar de baja el cultivo colindante"
        open={closeModalOpen}
        onCancel={() => (closing ? undefined : setCloseModalOpen(false))}
        onOk={handleConfirmClose}
        confirmLoading={closing}
        okText="Dar de baja"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>
          Marca el cultivo{" "}
          <strong>{cropForClose?.name}</strong> como cerrado. Indica
          brevemente el motivo (ya no existe, arrancado, fusionado…).
        </p>
        <Input.TextArea
          rows={3}
          value={closeReason}
          onChange={(e) => setCloseReason(e.target.value)}
          placeholder="Motivo del cierre…"
          maxLength={500}
          showCount
        />
      </Modal>

      {/* Photo Gallery + Upload Modal */}
      {selectedAdjacentCrop && (
        <PhotoGalleryModal
          visible={photoGalleryVisible}
          onClose={() => {
            setPhotoGalleryVisible(false);
            setSelectedAdjacentCrop(null);
          }}
          entityId={selectedAdjacentCrop.id}
          entityName={selectedAdjacentCrop.name}
          entityType="adjacentCrop"
          onPhotoDeleted={handlePhotoDeleted}
          onPhotoUploaded={() => {
            // Refresh list so photo_count + history pick up the new
            // photo / auto-created review.
            loadData();
            onRefresh?.();
          }}
          upload={
            readOnly
              ? undefined
              : {
                  auditId,
                  getTargetId: async () => {
                    const crop =
                      data.find((c) => c.id === selectedAdjacentCrop.id);
                    if (!crop) {
                      throw new Error(
                        "No se encontró el cultivo en la lista actual.",
                      );
                    }
                    return resolveAdjacentCropReviewId(crop);
                  },
                }
          }
        />
      )}
    </>
  );
};
